import type { InsightsHistoryEntry } from "../types";

const STORAGE_KEY = "aunix.insights.threads.v1";

/** Same-tab signal — `storage` only fires across tabs. */
export const INSIGHTS_THREADS_CHANGED_EVENT = "aunix.insights.threads.changed";

function notifyThreadsChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(INSIGHTS_THREADS_CHANGED_EVENT));
}

function readAll(): InsightsHistoryEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as InsightsHistoryEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(entries: InsightsHistoryEntry[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, 100)));
  notifyThreadsChanged();
}

export function listInsightsThreads(): InsightsHistoryEntry[] {
  return readAll().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/** Short prompts that must not replace the investigation title (length only — no ack-word list). */
export function isEphemeralThreadTitle(prompt: string): boolean {
  const cleaned = prompt.replace(/\s+/g, " ").trim();
  return !cleaned || cleaned.length <= 24;
}

export function upsertInsightsThread(
  entry: Omit<InsightsHistoryEntry, "createdAt" | "updatedAt"> & {
    createdAt?: string;
    title?: string;
  },
): InsightsHistoryEntry {
  const now = new Date().toISOString();
  const all = readAll();
  const existing = all.find((e) => e.threadId === entry.threadId);
  const incoming = entry.title?.trim() ?? "";
  const keepExisting =
    Boolean(existing?.title) &&
    existing!.title !== "New investigation" &&
    (isEphemeralThreadTitle(incoming) || !incoming);
  const next: InsightsHistoryEntry = {
    threadId: entry.threadId,
    title: keepExisting ? existing!.title : incoming || existing?.title || "New investigation",
    createdAt: existing?.createdAt ?? entry.createdAt ?? now,
    updatedAt: now,
  };
  writeAll([next, ...all.filter((e) => e.threadId !== entry.threadId)]);
  return next;
}

export function renameInsightsThread(threadId: string, title: string) {
  const all = readAll();
  const next = all.map((e) =>
    e.threadId === threadId
      ? { ...e, title: title.trim() || e.title, updatedAt: new Date().toISOString() }
      : e,
  );
  writeAll(next);
}

export function deleteInsightsThread(threadId: string) {
  writeAll(readAll().filter((e) => e.threadId !== threadId));
}

export function titleFromPrompt(prompt: string): string {
  const cleaned = prompt.replace(/\s+/g, " ").trim();
  if (!cleaned) return "New investigation";
  return cleaned.length > 60 ? `${cleaned.slice(0, 57)}…` : cleaned;
}
